import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, ChevronsLeftRight } from "lucide-react";
import { trackEstimate } from "@/lib/track";

const SCOPES = {
  "villa-palm-jumeirah": "Layout • Kitchen • Bathrooms • Marble Flooring • Exterior",
  "penthouse-downtown": "Custom Joinery • Smart Lighting • Ceilings • Bathrooms",
  "apartment-marina": "Open-Plan Kitchen • MEP • Flooring • Painting",
  "kitchen-jumeirah": "Cabinetry • Quartz Island • Lighting • Appliances",
  "bathroom-business-bay": "Plumbing • Large-Format Tiles • Vanity • Fixtures",
  "joinery-jbr": "Wardrobes • TV Wall • Cladding • Herringbone Flooring",
};

const PROJECTS = [
  {
    id: "villa-palm-jumeirah",
    tag: "Villa Renovation",
    title: "Luxury Villa Renovation – Palm Jumeirah",
    location: "Palm Jumeirah",
    before: "/images/gallery/villa-before.jpg",
    after: "/images/gallery/villa-after.jpg",
    alt: "Before and after luxury villa renovation Palm Jumeirah by Fix Technical Renovation Company",
    text: "Full layout reconfiguration, designer kitchen, premium bathrooms, marble flooring, and exterior upgrade — delivered with European-standard finishing and turn-key project supervision.",
  },
  {
    id: "penthouse-downtown",
    tag: "Penthouse Fit-Out",
    title: "Premium Penthouse Fit-Out – Downtown Dubai",
    location: "Downtown Dubai",
    before: "/images/gallery/penthouse-before.jpg",
    after: "/images/gallery/penthouse-after.jpg",
    alt: "Before and after premium penthouse fit-out Downtown Dubai by Fix Technical Renovation Company",
    text: "High-end penthouse fit-out with custom joinery, smart lighting, designer ceilings, and luxury bathrooms — completed on schedule with full authority approvals.",
  },
  {
    id: "apartment-marina",
    tag: "Apartment Renovation",
    title: "High-End Apartment Renovation – Dubai Marina (Time Place Tower)",
    location: "Dubai Marina",
    image: "/images/gallery/apartment-modern.jpg",
    alt: "High-end apartment renovation Dubai with open-plan kitchen and Burj Khalifa view by Fix Technical Renovation Company",
    text: "Turnkey remodeling of a Time Place Tower residence — open-plan kitchen conversion, premium MEP redesign, building management approvals secured in 4 days.",
  },
  {
    id: "kitchen-jumeirah",
    tag: "Bespoke Kitchen",
    title: "Bespoke Kitchen Remodeling – Jumeirah",
    location: "Jumeirah",
    image: "/images/gallery/kitchen-after.jpg",
    alt: "Bespoke kitchen remodeling Dubai by Fix Technical Renovation Company",
    text: "Custom European-style cabinetry, quartz countertops, island counter, designer lighting and integrated appliances — premium finishing throughout.",
  },
  {
    id: "bathroom-business-bay",
    tag: "Luxury Bathroom",
    title: "Luxury Bathroom Upgrade – Business Bay",
    location: "Business Bay",
    before: "/images/gallery/bathroom-before.jpg",
    after: "/images/gallery/bathroom-after.jpg",
    alt: "Before and after luxury bathroom renovation Business Bay by Fix Technical Renovation Company",
    text: "Concealed plumbing, large-format tiles, designer shower glass, custom vanity, gypsum ceiling and premium fixtures — engineered for elegance and durability.",
  },
  {
    id: "joinery-jbr",
    tag: "Joinery & Flooring",
    title: "Custom Joinery & Flooring – Jumeirah Beach Residence",
    location: "Jumeirah Beach Residence",
    image: "/images/gallery/joinery-after.jpg",
    alt: "Custom joinery and premium herringbone flooring Dubai by Fix Technical Renovation Company",
    text: "Full apartment joinery package — wardrobes, TV unit, wall cladding, kitchen cabinetry, and herringbone wooden flooring with luxury finishing.",
  },
];

function BeforeAfterImage({ project }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(94, Math.max(6, pct)));
  };

  return (
    <div
      ref={ref}
      data-testid={`ba-slider-${project.id}`}
      role="slider"
      aria-label={`Before and after comparison: ${project.title}`}
      aria-valuenow={Math.round(pos)}
      aria-valuemin={0}
      aria-valuemax={100}
      className="relative aspect-[5/4] overflow-hidden bg-neutral-100 select-none cursor-ew-resize"
      style={{ touchAction: "pan-y" }}
      onPointerDown={(e) => {
        dragging.current = true;
        ref.current.setPointerCapture(e.pointerId);
        update(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && update(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
      onPointerCancel={() => (dragging.current = false)}
    >
      {/* AFTER (base layer) */}
      <img
        src={project.after}
        alt={project.alt}
        loading="lazy"
        width="1200"
        height="960"
        draggable="false"
        data-testid={`ba-after-${project.id}`}
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* BEFORE (clipped top layer) */}
      <img
        src={project.before}
        alt=""
        aria-hidden="true"
        loading="lazy"
        width="1200"
        height="960"
        draggable="false"
        data-testid={`ba-before-${project.id}`}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      />

      {/* Handle */}
      <div
        data-testid={`ba-handle-${project.id}`}
        className="absolute top-0 bottom-0 w-px bg-white/90 shadow-[0_0_12px_rgba(0,0,0,0.5)]"
        style={{ left: `${pos}%` }}
      >
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white text-neutral-900 flex items-center justify-center shadow-xl border border-neutral-200">
          <ChevronsLeftRight className="w-5 h-5" />
        </div>
      </div>

      {/* Chips + gradient (non-interactive) */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
      <span className="pointer-events-none absolute top-4 left-4 bg-neutral-900/85 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-white">
        Before
      </span>
      <span className="pointer-events-none absolute top-4 right-4 bg-[#C5A059] px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-white">
        After
      </span>
      <span className="pointer-events-none absolute bottom-4 left-4 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-neutral-700">
        {project.tag}
      </span>
    </div>
  );
}

export default function Portfolio() {
  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="py-20 lg:py-32 bg-[#F9F8F6]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-6 mb-12 lg:mb-16">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#C5A059] font-semibold mb-3">
              Completed Projects · Drag to Compare
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-neutral-900 leading-[1.1]">
              See What a Complete Renovation Can Look Like.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 flex items-end">
            <p className="text-sm lg:text-base text-neutral-600 leading-relaxed">
              Explore selected apartment, home and interior renovation projects completed by our team — drag the slider on a project to compare before and after.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {PROJECTS.map((p) => (
            <article
              key={p.id}
              data-testid={`portfolio-card-${p.id}`}
              className="group bg-white border border-neutral-200 overflow-hidden transition-all duration-500 hover:shadow-[0_28px_60px_rgba(0,0,0,0.08)] hover:-translate-y-1"
            >
              {p.before ? (
                <BeforeAfterImage project={p} />
              ) : (
                <div className="relative aspect-[5/4] overflow-hidden bg-neutral-100" data-testid={`portfolio-image-${p.id}`}>
                  <img
                    src={p.image}
                    alt={p.alt}
                    loading="lazy"
                    width="1200"
                    height="960"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
                  <span className="pointer-events-none absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-neutral-700">
                    {p.tag}
                  </span>
                </div>
              )}
              <div className="px-6 py-6">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-2">{p.location}</p>
                <h3 className="font-heading text-lg lg:text-xl font-medium text-neutral-900 leading-tight mb-2">
                  {p.title}
                </h3>
                <p className="text-[11px] text-neutral-500 mb-3">{SCOPES[p.id]}</p>
                <p className="text-sm text-neutral-600 leading-relaxed mb-5">{p.text}</p>
                <a href="#lead-form" data-testid={`portfolio-cta-${p.id}`}>
                  <Button
                    variant="outline"
                    className="rounded-none border-neutral-900 text-neutral-900 hover:bg-[#C5A059] hover:border-[#C5A059] hover:text-white py-5 w-full"
                  >
                    Request a Similar Project
                    <ArrowUpRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            </article>
          ))}
        </div>

        <div className="text-center mt-10 lg:mt-12">
          <a href="#lead-form" data-testid="portfolio-cta" onClick={() => trackEstimate("portfolio_section")}>
            <Button className="bg-[#C5A059] hover:bg-[#A88746] text-white rounded-none px-8 py-7 font-semibold tracking-wide">
              GET MY ROUGH ESTIMATE
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
}
